import { orderBy } from "firebase/firestore";
import queryString from "query-string";
import React from "react";
import {
    Alert,
    Badge,
    Button,
    Card,
    Dropdown,
    Form,
    ListGroup,
    ListGroupItem,
    Modal,
    OverlayTrigger,
    Spinner,
    Tooltip,
} from "react-bootstrap";
import {
    FaCrown,
    FaEnvelope,
    FaPlus,
    FaUser,
    FaUserCheck,
    FaUserEdit,
    FaUsersSlash,
    FaUserTimes,
} from "react-icons/fa";
import { MdOutlineLockReset, MdOutlineMoreVert } from "react-icons/md";
import { Link, useNavigate } from "react-router";

import * as api from "../../api/api";
import { useOrganizationCollection } from "../../api/firestoreWrappers.generated.jsx";
import Layout, { Title } from "../../components/Layout/Layout";
import TimeDisplay from "../../components/TimeDisplay/index.jsx";
import UserFilters from "../../components/UserFilters/index.jsx";
import { useAuth } from "../../contexts/auth_provider";
import useIsSmallScreen from "../../hooks/useIsSmallScreen.jsx";
import urls from "../../urls.jsx";

const filterOptions = [
    [
        {
            value: "admin",
            label: "Admin",
            filter: (user) => user?.customClaims?.isAdmin === true,
        },
        {
            value: "notAdmin",
            label: "Not admin",
            filter: (user) => user?.customClaims?.isAdmin === false,
        },
    ],
    // [
    //     { value: 'emailVerified', label: 'E-mail verified', filter: (user) => user?.emailVerified === true },
    //     { value: 'emailNotVerified', label: 'E-mail not verified', filter: (user) => user?.emailVerified === false },
    // ],
    [
        {
            value: "disabled",
            label: "Disabled",
            filter: (user) => user?.disabled === true,
        },
        {
            value: "notDisabled",
            label: "Not Disabled",
            filter: (user) => user?.disabled === false,
        },
    ],
];

const sortOptions = [
    { value: "lastOnline", label: "Last Online" },
    { value: "created", label: "Created" },
    { value: "alphabetical", label: "Alphabetical" },
    { value: "organization", label: "Organization" },
];

export default function UserManagementPage() {
    const [organizations] = useOrganizationCollection(
        [orderBy("name", "asc")],
        { type: "map" },
    );
    const [allUsers, setAllUsers] = React.useState([]);
    const [resetPasswordModalVisible, setResetPasswordModalVisible] =
        React.useState(false);
    const [resetPasswordEmail, setResetPasswordEmail] = React.useState(null);
    const [isSendingResetEmail, setIsSendingResetEmail] = React.useState(false);

    const [showDisableUserModal, setShowDisableUserModal] =
        React.useState(false);
    const [userToBeDisabled, setUserToBeDisabled] = React.useState(null);
    const [isDisablingUser, setIsDisablingUser] = React.useState(false);

    const { resetPasswordLink } = useAuth();
    const [error, setError] = React.useState("");
    const navigate = useNavigate();
    const isSmallScreen = useIsSmallScreen(670);

    const [isLoading, setIsLoading] = React.useState(true);

    const queryParams = queryString.parse(location.search);

    const [order, setOrder] = React.useState(
        queryParams.order === "asc" || queryParams.order === "desc"
            ? queryParams.order
            : "asc",
    );

    const [sortCriteria, setSortCriteria] = React.useState(
        sortOptions.find((opt) => opt.value === queryParams.sortCriteria) ||
            sortOptions[0],
    );

    const [searchQuery, setSearchQuery] = React.useState(
        queryParams.searchQuery || "",
    );

    const singleFilters = (
        Array.isArray(queryParams.singleFilterValues)
            ? queryParams.singleFilterValues
            : (queryParams.singleFilterValues?.split(",") ?? [])
    )
        .map((v) => filterOptions.flat().find((opt) => opt.value === v))
        .filter(Boolean);

    const [filters, setFilters] = React.useState({
        organizationId: queryParams.organizationId || undefined,
        singleFilters,
    });

    const fetchUsers = React.useCallback(async () => {
        setIsLoading(true);
        try {
            const users = await api.getAllUsers();
            setAllUsers(Object.values(users));
        } catch (error) {
            setError(error.message ?? String(error));
        } finally {
            setIsLoading(false);
        }
    }, []);

    const updateSingleUser = React.useCallback(async (uid) => {
        try {
            const updatedUser = await api.getSingleUserDetails(uid);
            if (!updatedUser) return;
            setAllUsers((prev) =>
                prev.map((u) => (u.uid === uid ? updatedUser : u)),
            );
        } catch (error) {
            setError(error.message ?? String(error));
        }
    }, []);

    React.useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handledUsers = React.useMemo(() => {
        if (!allUsers.length) return [];

        // filter
        const users = allUsers.filter(
            (user) =>
                (!filters.organizationId ||
                    user?.customClaims?.organizationId ===
                        filters.organizationId) &&
                (!filters.singleFilters ||
                    filters.singleFilters.every((f) => f.filter(user))) &&
                user.email.toLowerCase().includes(searchQuery.toLowerCase()),
        );

        //sort
        const dir = order === "asc" ? 1 : -1;

        const toTime = (value) => {
            if (!value) return 0;
            const t = Date.parse(value);
            return Number.isNaN(t) ? 0 : t;
        };

        users.sort((a, b) => {
            switch (sortCriteria.value) {
                case "alphabetical":
                    return dir * a.email.localeCompare(b.email);

                case "organization": {
                    const orgA =
                        organizations[a.customClaims?.organizationId]?.name ??
                        "";
                    const orgB =
                        organizations[b.customClaims?.organizationId]?.name ??
                        "";
                    return dir * orgA.localeCompare(orgB);
                }

                case "lastOnline":
                    return (
                        dir *
                        (toTime(b.metadata?.lastRefreshTime) -
                            toTime(a.metadata?.lastRefreshTime))
                    );

                default:
                    return (
                        dir *
                        (toTime(b.metadata?.creationTime) -
                            toTime(a.metadata?.creationTime))
                    );
            }
        });

        return users;
    }, [
        allUsers,
        sortCriteria.value,
        order,
        organizations,
        filters.organizationId,
        filters.singleFilters,
        searchQuery,
    ]);

    const totalUsers = handledUsers.length;
    const adminUsers = handledUsers.filter(
        (user) => user?.customClaims?.isAdmin,
    ).length;
    const disabledUsers = handledUsers.filter((user) => user?.disabled).length;

    React.useEffect(() => {
        const singleFilterValuesArray =
            filters.singleFilters
                ?.map((f) => f.value)
                .filter((v) => v != null) || [];

        const newQuery = {
            searchQuery: searchQuery.length > 0 ? searchQuery : undefined,
            organizationId: filters.organizationId || undefined,
            ...(singleFilterValuesArray.length > 0 && {
                singleFilterValues: singleFilterValuesArray.join(","),
            }),
            order: order,
            sortCriteria: sortCriteria.value,
        };

        const cleanedQuery = Object.fromEntries(
            Object.entries(newQuery).filter(([, v]) => v !== undefined),
        );

        const params = new URLSearchParams(cleanedQuery);
        const queryString = params.toString();
        navigate(`?${queryString}`, { replace: true });
    }, [filters, navigate, searchQuery, order, sortCriteria]);

    function formatDate(date) {
        return (
            <TimeDisplay
                timestamp={Math.floor(
                    (typeof date === "string"
                        ? new Date(date)
                        : date
                    ).getTime() / 1000,
                )}
                format="D MMM YYYY#HH:mm"
                breakOnHashtag={isSmallScreen}
            />
        );
    }

    function OverlayTriggerContainer({
        creationTime,
        lastRefreshTime,
        children,
    }) {
        return (
            <OverlayTrigger
                delay={{ show: 500 }}
                overlay={
                    <Tooltip>
                        <span>
                            Last Online: <br />
                            {lastRefreshTime
                                ? formatDate(lastRefreshTime)
                                : "unknown"}
                            <br />
                            Created: <br />
                            {creationTime
                                ? formatDate(creationTime)
                                : "unknown"}
                        </span>
                    </Tooltip>
                }
                popperConfig={{
                    modifiers: [
                        { name: "offset", options: { offset: [0, 10] } },
                    ],
                }}
            >
                {children}
            </OverlayTrigger>
        );
    }

    const dropdownToggle = React.forwardRef(({ onClick }, ref) => (
        <button
            type="button"
            ref={ref}
            onClick={(e) => {
                e.preventDefault();
                onClick(e);
            }}
            className="p-0 bg-transparent border-0 d-flex align-items-center h-100"
        >
            <MdOutlineMoreVert size={20} />
        </button>
    ));
    dropdownToggle.displayName = "dropdownToggle";

    const hideResetPasswordModal = () => {
        setResetPasswordModalVisible(false);
        setResetPasswordEmail(null);
    };

    const setUserDisabled = async (uid, disabled) => {
        try {
            await api.setUserDisabled(uid, disabled);
            await updateSingleUser(uid);
        } catch (error) {
            setError(error.message ?? String(error));
        }
    };

    const closeDisableUserModal = () => {
        setShowDisableUserModal(false);
        setUserToBeDisabled(null);
    };

    return (
        <>
            <Modal
                show={showDisableUserModal}
                onHide={closeDisableUserModal}
                backdrop={isDisablingUser ? "static" : true}
                keyboard={!isDisablingUser}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="d-flex align-items-center">
                        <FaUserTimes size={30} className="me-2" /> Disable user
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to disable user{" "}
                    <strong>{userToBeDisabled?.email}</strong>?
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-secondary"
                        onClick={closeDisableUserModal}
                    >
                        Cancel
                    </Button>
                    <Dropdown>
                        <Button
                            disabled={isDisablingUser}
                            onClick={async () => {
                                setIsDisablingUser(true);
                                await setUserDisabled(
                                    userToBeDisabled?.uid,
                                    true,
                                );
                                closeDisableUserModal();
                                setIsDisablingUser(false);
                            }}
                        >
                            <FaUserTimes
                                className="me-2"
                                style={{ marginTop: "-4px" }}
                            />
                            {isDisablingUser ? "Disabling..." : "Disable"}
                        </Button>
                    </Dropdown>
                </Modal.Footer>
            </Modal>
            <Modal
                show={resetPasswordModalVisible}
                onHide={hideResetPasswordModal}
                backdrop={isSendingResetEmail ? "static" : true}
                keyboard={!isSendingResetEmail}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="d-flex align-items-center">
                        <MdOutlineLockReset size={30} className="me-2" /> Send
                        password reset email
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    This will send an email to{" "}
                    <strong>{resetPasswordEmail}</strong> with a password reset
                    link.
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-secondary"
                        onClick={hideResetPasswordModal}
                    >
                        Cancel
                    </Button>
                    <Dropdown>
                        <Button
                            disabled={isSendingResetEmail}
                            onClick={async () => {
                                setIsSendingResetEmail(true);
                                try {
                                    await resetPasswordLink(resetPasswordEmail);
                                    hideResetPasswordModal();
                                } catch (error) {
                                    setError(error.message ?? String(error));
                                } finally {
                                    setIsSendingResetEmail(false);
                                }
                            }}
                        >
                            <FaEnvelope
                                className="me-2"
                                style={{ marginTop: "-4px" }}
                            />
                            {isSendingResetEmail ? "Sending..." : "Send"}
                        </Button>
                    </Dropdown>
                </Modal.Footer>
            </Modal>
            <Layout>
                <title>Users | MyCorvus</title>

                <Card className="mb-4">
                    <Title title="Users">
                        <Button
                            variant="outline-success"
                            as={Link}
                            to={urls.userCreate()}
                            className="ms-2 mb-1"
                        >
                            <FaPlus className="me-2" />
                            New User
                        </Button>
                    </Title>
                </Card>

                {error && (
                    <Alert
                        variant="danger"
                        onClose={() => setError("")}
                        dismissible
                    >
                        {error}
                    </Alert>
                )}

                <Form className="mb-2 d-flex justify-content-between w-100">
                    <UserFilters
                        filters={filters}
                        setFilters={setFilters}
                        order={order}
                        setOrder={setOrder}
                        sortCriteria={sortCriteria}
                        setSortCriteria={setSortCriteria}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        filterOptions={filterOptions}
                        sortOptions={sortOptions}
                    />
                </Form>

                {!isLoading && totalUsers > 0 && (
                    <div className="d-flex align-items-center gap-4 mb-2 ms-3">
                        {adminUsers === 0 && disabledUsers === 0 && (
                            <div className="d-flex align-items-center gap-1">
                                <FaUser /> {totalUsers} user
                                {totalUsers > 1 && "s"}
                            </div>
                        )}
                        {adminUsers > 0 && (
                            <div className="d-flex align-items-center gap-1">
                                <FaCrown className="text-success" />{" "}
                                {adminUsers}/{totalUsers} user
                                {totalUsers > 1 && "s"}
                            </div>
                        )}
                        {disabledUsers > 0 && (
                            <div className="d-flex align-items-center gap-1">
                                <FaUserTimes className="text-danger" />{" "}
                                {disabledUsers}/{totalUsers} user
                                {totalUsers > 1 && "s"}
                            </div>
                        )}
                    </div>
                )}

                <Card>
                    {isLoading ? (
                        <div className="d-flex justify-content-center py-3">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">
                                    Loading...
                                </span>
                            </Spinner>
                        </div>
                    ) : (
                        <ListGroup>
                            {handledUsers.length === 0 && (
                                <ListGroupItem variant="warning">
                                    <FaUsersSlash className="me-2" />{" "}
                                    <span className="fst-italic">
                                        No users match these filters
                                    </span>
                                </ListGroupItem>
                            )}
                            {handledUsers.map((user) => {
                                const orgId =
                                    user?.customClaims?.organizationId;
                                const organization = orgId
                                    ? organizations[orgId]
                                    : undefined;

                                const organizationName =
                                    organization?.name ?? orgId;
                                const organizationDeleted =
                                    !organization ||
                                    organization.deleted === true;

                                return (
                                    <OverlayTriggerContainer
                                        key={user.uid}
                                        creationTime={
                                            user?.metadata?.creationTime
                                        }
                                        lastRefreshTime={
                                            user?.metadata?.lastRefreshTime
                                        }
                                    >
                                        <ListGroupItem
                                            className={`d-flex justify-content-between ${user?.disabled && "text-muted"}`}
                                        >
                                            <div className="me-2">
                                                {user?.email} @{" "}
                                                <span
                                                    className={
                                                        organizationDeleted
                                                            ? "text-danger"
                                                            : undefined
                                                    }
                                                >
                                                    {organizationName}
                                                    {organizationDeleted &&
                                                        " (deleted organization)"}
                                                </span>
                                                {user?.disabled && (
                                                    <FaUserTimes className="mx-1 text-danger" />
                                                )}
                                                {/* {user?.emailVerified === false && <TbMailExclamation className="mx-1 text-warning" />} */}
                                                {user?.customClaims
                                                    ?.isAdmin && (
                                                    <FaCrown className="mx-1 text-success" />
                                                )}
                                            </div>

                                            <div className="d-flex align-items-center">
                                                {user?.metadata
                                                    ?.lastRefreshTime && (
                                                    <Badge pill bg="secondary">
                                                        {formatDate(
                                                            user?.metadata
                                                                ?.lastRefreshTime,
                                                        )}
                                                    </Badge>
                                                )}

                                                <Dropdown align="end">
                                                    <Dropdown.Toggle
                                                        as={dropdownToggle}
                                                    />
                                                    <Dropdown.Menu>
                                                        <Dropdown.Item
                                                            onClick={async () => {
                                                                if (
                                                                    user.disabled
                                                                ) {
                                                                    await setUserDisabled(
                                                                        user.uid,
                                                                        false,
                                                                    );
                                                                } else {
                                                                    setUserToBeDisabled(
                                                                        user,
                                                                    );
                                                                    setShowDisableUserModal(
                                                                        true,
                                                                    );
                                                                }
                                                            }}
                                                            className={`d-flex align-items-center text-${user?.disabled ? "success" : `danger`}`}
                                                        >
                                                            {user?.disabled ? (
                                                                <FaUserCheck className="me-2" />
                                                            ) : (
                                                                <FaUserTimes className="me-2" />
                                                            )}
                                                            {user?.disabled
                                                                ? "Enable"
                                                                : `Disable`}
                                                        </Dropdown.Item>
                                                        {!user.disabled && (
                                                            <Dropdown.Item
                                                                onClick={() => {
                                                                    setResetPasswordEmail(
                                                                        user?.email,
                                                                    );
                                                                    setResetPasswordModalVisible(
                                                                        true,
                                                                    );
                                                                }}
                                                                className="d-flex align-items-center"
                                                            >
                                                                <MdOutlineLockReset
                                                                    className="me-2"
                                                                    style={{
                                                                        marginLeft:
                                                                            "-4px",
                                                                    }}
                                                                    size={20}
                                                                />
                                                                Reset password
                                                            </Dropdown.Item>
                                                        )}
                                                        <Dropdown.Item
                                                            onClick={() =>
                                                                navigate(
                                                                    urls.userSettings(
                                                                        user.uid,
                                                                    ),
                                                                )
                                                            }
                                                            className="d-flex align-items-center"
                                                        >
                                                            <FaUserEdit className="me-2" />
                                                            Edit user
                                                        </Dropdown.Item>
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </div>
                                        </ListGroupItem>
                                    </OverlayTriggerContainer>
                                );
                            })}
                        </ListGroup>
                    )}
                </Card>
            </Layout>
        </>
    );
}
